// Import of the module

import { formatNumber, parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";

// Type of the fields in the table

export type Audit = {
  id: number;
  auditable_id?: number;
  auditable_type?: string;
  associated_id?: number;
  associated_type?: string;
  user_id?: number;
  user_type?: string;
  username?: string;
  user_firstname?: string;
  user_lastname?: string;
  user_email?: string;
  action?: string;
  audited_changes?: Record<string, any>;
  version?: number;
  comment?: string;
  k1_comment?: string;
  remote_address?: string;
  request_uuid?: string;
  created_at?: string;
};

// Definition of the columns

export const getAuditsColumns = (t: TFunction): ColumnDef<Audit>[] => [
  {
    accessorKey: "id",
    header: t("audits.columns.id", { ns: "tables", defaultValue: "ID" }),
  },
  {
    accessorKey: "user_id",
    header: t("audits.columns.user_id", {
      ns: "tables",
      defaultValue: "User ID",
    }),
    cell: ({ row }) => {
      const value = row.getValue("user_id") as string | number | null;
      return value ? <span>{String(value)}</span> : <span>&nbsp;</span>;
    },
  },
  {
    accessorKey: "user_type",
    header: t("audits.columns.user_type", {
      ns: "tables",
      defaultValue: "User type",
    }),
    cell: ({ row }) => {
      const value = row.getValue("user_type") as string | null;
      return value ? <span>{value}</span> : <span>&nbsp;</span>;
    },
  },
  {
    accessorKey: "user_firstname",
    header: t("audits.columns.user_firstname", {
      ns: "tables",
      defaultValue: "First name",
    }),
    cell: ({ row }) => {
      const value = row.getValue("user_firstname") as string | null;
      return value ? <span>{value}</span> : <span>&nbsp;</span>;
    },
  },
  {
    accessorKey: "user_lastname",
    header: t("audits.columns.user_lastname", {
      ns: "tables",
      defaultValue: "Last name",
    }),
    cell: ({ row }) => {
      const value = row.getValue("user_lastname") as string | null;
      return value ? <span>{value}</span> : <span>&nbsp;</span>;
    },
  },
  {
    accessorKey: "user_email",
    header: t("audits.columns.user_email", {
      ns: "tables",
      defaultValue: "Email",
    }),
    cell: ({ row }) => {
      const value = row.getValue("user_email") as string | null;
      return value ? <span>{value}</span> : <span>&nbsp;</span>;
    },
  },
  {
    accessorKey: "action",
    header: t("audits.columns.action", {
      ns: "tables",
      defaultValue: "Action",
    }),
    cell: ({ row }) => {
      const value = row.getValue("action") as string | null;
      return value ? <span>{value}</span> : <span>&nbsp;</span>;
    },
  },
  {
    accessorKey: "version",
    header: t("audits.columns.version", {
      ns: "tables",
      defaultValue: "Version",
    }),
    cell: ({ row }) =>
      row.getValue("version") !== null &&
      row.getValue("version") !== undefined ? (
        <span>{formatNumber(row.getValue("version"))}</span>
      ) : (
        <span className="text-muted-foreground italic">-</span>
      ),
  },
  {
    accessorKey: "comment",
    header: t("audits.columns.comment", {
      ns: "tables",
      defaultValue: "Comment",
    }),
    cell: ({ row }) => {
      const value = row.getValue("comment") as string | null;
      return value ? <span>{value}</span> : <span>&nbsp;</span>;
    },
  },
  {
    accessorKey: "k1_comment",
    header: t("audits.columns.k1_comment", {
      ns: "tables",
      defaultValue: "K1 Comment",
    }),
    cell: ({ row }) => {
      const value = row.getValue("k1_comment") as string | null;
      return value ? <span>{value}</span> : <span>&nbsp;</span>;
    },
  },
  {
    accessorKey: "created_at",
    header: t("audits.columns.created_at", {
      ns: "tables",
      defaultValue: "Created at",
    }),
    cell: ({ row }) =>
      row.getValue("created_at") ? (
        <span>{parseDate(row.getValue("created_at"))}</span>
      ) : (
        <span className="text-muted-foreground italic">-</span>
      ),
  },
  {
    accessorKey: "audited_changes",
    header: t("audits.columns.audited_changes", {
      ns: "tables",
      defaultValue: "Changes",
    }),
    cell: ({ row }) => {
      const changes = row.getValue("audited_changes");
      if (!changes || Object.keys(changes).length === 0) {
        return <span>&nbsp;</span>;
      }
      return (
        <pre className="max-w-xs overflow-x-auto whitespace-pre-wrap text-xs bg-muted/60 rounded p-2">
          {JSON.stringify(changes, null, 2)}
        </pre>
      );
    },
  },
  {
    id: "actions",
    header: () => (
      <div className="text-right">
        {t("audits.columns.actions", { ns: "tables", defaultValue: "Actions" })}
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex justify-end gap-2">
        <Link
          href={`${window.location.pathname}/${row.getValue("id")}`}
          className="inline-flex items-center gap-1 text-xs"
          underline="hover"
        >
          {t("actions.edit")}
        </Link>
      </div>
    ),
  },
];
